/**
 * The project list and the one mutation B2 performs.
 *
 * **Filters and sort are part of the query key, and they are part of the request.** Switching
 * a status chip refetches with a new `status` parameter rather than re-filtering a superset
 * the browser happens to hold: the list is cursor-paged, so a "superset" is only ever the
 * first page of one, and filtering it client-side would quietly hide every project past the
 * cursor (KLQ-20260819-003 acceptance criterion 3).
 *
 * The one deliberate exception is `owned` / `shared`. It is not a query parameter — the
 * endpoint computes `relation` per request from the caller's memberships and offers no filter
 * on it — so those two chips narrow what is already loaded. `ProjectsPage` does that, and the
 * gap is recorded in the spec's Notes rather than papered over here.
 *
 * Nothing polls and nothing subscribes. The list refetches when a project is created and when
 * a person changes a filter; that is the whole invalidation story (CLAUDE.md, data fetching).
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';

import type {
  Project,
  ProjectCreate,
  ProjectListQuery,
  ProjectObject,
  ProjectObjectCreate,
  ProjectObjectUpdate,
  ProjectPage,
  ProjectPhase,
  ProjectPhaseCreate,
  ProjectPhaseUpdate,
} from './api';
import {
  archiveProjectObject,
  createProject,
  createProjectObject,
  createProjectPhase,
  deleteProjectPhase,
  listProjectObjects,
  listProjectPhases,
  listProjects,
  readProject,
  readProjectSurfaces,
  setCurrentPhase,
  updateProjectObject,
  updateProjectPhase,
} from './api';

/** Everything about projects hangs off this prefix, so one invalidation clears it all. */
export const PROJECTS_QUERY_KEY = ['projects'] as const;

/** The key for one filter/sort combination. Both values are in it because both are in the URL. */
export function projectListQueryKey(query: ProjectListQuery) {
  return [...PROJECTS_QUERY_KEY, 'list', query.status, query.sort] as const;
}

/**
 * The paged list.
 *
 * `getNextPageParam` reads `has_more` rather than testing `next_cursor` for emptiness: the
 * server is the one that knows whether a further page exists, and a cursor present on a last
 * page would otherwise buy an empty round trip.
 */
export function useProjectList(query: ProjectListQuery) {
  return useInfiniteQuery({
    queryKey: projectListQueryKey(query),
    queryFn: ({ pageParam }) => listProjects(query, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ProjectPage) => (last.has_more ? (last.next_cursor ?? null) : null),
    // A project list changes when somebody creates or edits a project, which is not often and
    // is invalidated explicitly when this tab is the one that did it.
    staleTime: 60_000,
  });
}

/** One project, for the stub project route. */
export function projectQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...PROJECTS_QUERY_KEY, 'detail', projectId] as const,
    queryFn: () => readProject(projectId),
    staleTime: 60_000,
  });
}

export function useProject(projectId: string) {
  return useQuery(projectQueryOptions(projectId));
}

/**
 * The caller's tab row for one project.
 *
 * Reference data for the length of a visit: one request when the shell mounts, shared by
 * every tab through the cache, never polled and never subscribed to. Five minutes of
 * staleness is safe because this decides *rendering* only — the server re-resolves authority
 * on every request behind every tab, so a membership change takes effect on the next call
 * whatever the tab row currently believes (`MCK-20260812-004` §2 is met by the server, not
 * by this cache).
 *
 * The one thing that invalidates it early is a role change the caller made **to themselves**;
 * `features/people` does that explicitly.
 */
export function projectSurfacesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...PROJECTS_QUERY_KEY, 'surfaces', projectId] as const,
    queryFn: () => readProjectSurfaces(projectId),
    staleTime: 5 * 60_000,
  });
}

export function useProjectSurfaces(projectId: string) {
  return useQuery(projectSurfacesQueryOptions(projectId));
}

export interface CreateProjectVariables {
  readonly body: ProjectCreate;
  /** A UUID, minted by the caller and reused for a retry of the same submission. */
  readonly idempotencyKey: string;
  /**
   * Which company the project belongs to. Chosen in the drawer when the caller has authority
   * in more than one, and the tab's active workspace otherwise — never left to the server to
   * guess, because a project's owning workspace is immutable (`KLQ-20260819-007` §3).
   */
  readonly workspaceId: string | null;
}

/**
 * Create, then forget every cached list.
 *
 * `invalidateQueries` on the whole prefix rather than writing the new project into the page
 * the screen is showing: which lists the project belongs in is a function of the filter and
 * the sort of each of them, and a client that decides that for itself is a client that puts a
 * project in the wrong place the first time a sort is added.
 */
export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation<Project, Error, CreateProjectVariables>({
    mutationFn: ({ body, idempotencyKey, workspaceId }) =>
      createProject(body, idempotencyKey, workspaceId),
    // The client's own 401 rotation has already run by the time an error reaches here, and a
    // retried create is exactly what the idempotency key exists to make safe — but a retry on
    // a `409` would only earn the same `409` twice.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    },
  });
}

// --- Phases and the norm catalogue (`KLQ-20260827-002`) --------------------------------

/**
 * The project's phase set.
 *
 * **One request per project visit, and the phase bar is its only fetcher.** Every surface
 * below reads the same cache entry through `usePhaseContext`, which is what makes switching
 * phase free: the selection lives in the URL, the phases live here, and neither is a query
 * parameter of anything the surfaces fetch.
 *
 * Five minutes of staleness, like the surface matrix: the set changes when somebody enables
 * or archives a rung, and the mutations below invalidate it themselves when this tab is the
 * one that did it.
 */
export function projectPhasesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...PROJECTS_QUERY_KEY, 'phases', projectId] as const,
    queryFn: () => listProjectPhases(projectId),
    staleTime: 5 * 60_000,
  });
}

export function useProjectPhases(projectId: string) {
  return useQuery(projectPhasesQueryOptions(projectId));
}

// The norm catalogue and the studio's enabled set are NOT queried here. `features/setup` owns
// `normCatalogueQueryOptions` (one entry, `staleTime: Infinity`, shared by B0, Studio settings
// and now the phase bar) and `features/norms` owns `workspaceNormsQueryOptions`. Two keys for
// one resource is two answers to one question, and for a list that is byte-identical in every
// tenant it is also a second fetch of something already held.

/** Enable one more rung. A second instance of a definition needs a `label_override` — `422`. */
export function useCreateProjectPhase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectPhase, Error, ProjectPhaseCreate>({
    mutationFn: (body) => createProjectPhase(projectId, body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'phases', projectId],
      });
    },
  });
}

/** Rename or archive a rung. Archiving clears `current_phase_id`, so the project is stale too. */
export function useUpdateProjectPhase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectPhase, Error, { phaseId: string; body: ProjectPhaseUpdate }>({
    mutationFn: ({ phaseId, body }) => updateProjectPhase(projectId, phaseId, body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'phases', projectId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'detail', projectId],
      });
    },
  });
}

/**
 * The phase a new drawing lands in.
 *
 * It invalidates the **phase list** as well as the project, because `is_current` is carried on
 * every phase row: writing only the project would leave the bar drawing its `●` on the rung
 * that used to be current until something else happened to refetch.
 */
export function useSetCurrentPhase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string | null>({
    mutationFn: (phaseId) => setCurrentPhase(projectId, phaseId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'phases', projectId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'detail', projectId],
      });
    },
  });
}

/**
 * Remove a rung the project never used.
 *
 * `409` when a drawing is filed under it, which is the vocabulary rule of `MCK-20260813-003`
 * §2: a referenced entry archives, it never disappears from under the rows that point at it.
 * The caller turns that refusal into the offer to archive instead; it is a fork in the flow,
 * not a failure to report.
 */
export function useDeleteProjectPhase(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (phaseId) => deleteProjectPhase(projectId, phaseId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'phases', projectId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...PROJECTS_QUERY_KEY, 'detail', projectId],
      });
    },
  });
}

// --- The object breakdown (`KLQ-20260817-002`) ----------------------------------------
//
// **This is not reference data and it is not cached like it.** A discipline list and a norm
// ladder are the same rows for everybody and change when a law does; a composition is written
// by the architect *during* the project, one object at a time, exactly as they discover it —
// so the only sensible staleness is "as of the last thing anybody did to it". Thirty seconds,
// and the three mutations below invalidate it themselves.

function objectsKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, 'objects', projectId] as const;
}

export function projectObjectsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: objectsKey(projectId),
    queryFn: () => listProjectObjects(projectId),
    staleTime: 30_000,
  });
}

export function useProjectObjects(projectId: string, enabled = true) {
  return useQuery({ ...projectObjectsQueryOptions(projectId), enabled });
}

/**
 * Add one object.
 *
 * `retry: false` for the reason the project create states: a `409` earned twice is still a
 * `409`, and this one means a number, a file code or the main flag is already taken — which a
 * retry cannot change and the person has to see.
 */
export function useCreateProjectObject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectObject, Error, ProjectObjectCreate>({
    mutationFn: (body) => createProjectObject(projectId, body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: objectsKey(projectId) });
    },
  });
}

/** Edit one object. A `409` here is the lock, not a race — the number is already on a sheet. */
export function useUpdateProjectObject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectObject, Error, { objectId: string; body: ProjectObjectUpdate }>({
    mutationFn: ({ objectId, body }) => updateProjectObject(projectId, objectId, body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: objectsKey(projectId) });
    },
  });
}

/** Grey one out. There is no delete anywhere in this file, and that is the vocabulary rule. */
export function useArchiveProjectObject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<ProjectObject, Error, string>({
    mutationFn: (objectId) => archiveProjectObject(projectId, objectId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: objectsKey(projectId) });
    },
  });
}
